import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { Card } from '@/components/ui';
import { useAdminPermission } from '@/lib/useAdminPermission';

/**
 * EditIdentityControl — the customer-management write in CustomerDrawer. Lets a
 * staffer rewrite a customer's DISPLAY identity (first name, last name, email).
 * The write PATCHes admin-svc, which proxies auth-svc update-identity (auth-svc
 * owns identity); phone is the login key and is shown READ-ONLY here — it can't
 * be changed without breaking auth.
 *
 * RBAC: only ops + superadmin hold customers.manage, so others don't see the
 * form. (admin-svc enforces the same matrix and 403s regardless — a UX
 * affordance, not the security boundary.)
 *
 * email is unique per scope server-side: a collision returns a 409, surfaced
 * here as "That email is already used by another customer." An empty email is
 * allowed and clears it. On success it invalidates ['admin','customer',id] so
 * the drawer refreshes from the authoritative source.
 */
export function EditIdentityControl({
  id,
  firstName,
  lastName,
  email,
  phone,
}: {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  /** Display phone (with country code) — shown read-only as the login ID. */
  phone: string;
}) {
  const canManage = useAdminPermission('customers.manage');
  const qc = useQueryClient();
  const [first, setFirst] = useState(firstName);
  const [last, setLast] = useState(lastName);
  const [mail, setMail] = useState(email);

  const mutation = useMutation({
    mutationFn: () =>
      adminApi.editCustomerIdentity(id, {
        first_name: first.trim(),
        last_name: last.trim(),
        email: mail.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customer', id] });
    },
  });

  // Hide entirely for roles without the capability (server enforces too).
  if (!canManage) return null;

  // Only enable Save when something actually changed (trimmed compare).
  const dirty =
    first.trim() !== firstName.trim() || last.trim() !== lastName.trim() || mail.trim() !== email.trim();

  return (
    <Card>
      <div className="text-sm font-medium text-slate-100">Edit identity</div>
      <p className="mt-1 text-xs text-muted">
        Updates the customer&apos;s name and email. Phone is the login ID and can&apos;t be changed here. Audited.
      </p>

      <div className="mt-3 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="min-w-0">
            <label className="block text-[11px] text-text-secondary">First name</label>
            <input
              type="text"
              value={first}
              onChange={(e) => {
                setFirst(e.target.value);
                mutation.reset();
              }}
              className="input mt-1"
            />
          </div>
          <div className="min-w-0">
            <label className="block text-[11px] text-text-secondary">Last name</label>
            <input
              type="text"
              value={last}
              onChange={(e) => {
                setLast(e.target.value);
                mutation.reset();
              }}
              className="input mt-1"
            />
          </div>
        </div>

        <div className="min-w-0">
          <label className="block text-[11px] text-text-secondary">Email</label>
          <input
            type="email"
            value={mail}
            onChange={(e) => {
              setMail(e.target.value);
              mutation.reset();
            }}
            placeholder="—"
            className="input mt-1"
          />
        </div>

        <div className="min-w-0">
          <label className="block text-[11px] text-text-secondary">Phone (login ID — can&apos;t be changed here)</label>
          <input type="text" value={phone} readOnly disabled className="input mt-1 cursor-not-allowed opacity-60" />
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={mutation.isPending || !dirty}
            onClick={() => mutation.mutate()}
            className="btn-primary"
          >
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </button>
          {mutation.isSuccess ? <span className="text-xs text-ok">Saved.</span> : null}
          {mutation.isError ? (
            <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
